import type { IFormComponent } from "@/modules/Administracion/interfaces"
import type { IConfOdontogramas, IConfOdontogramasConvenciones } from "@/modules/Configuracion/interfaces"
import { computed, getCurrentInstance, onUnmounted, ref, type Ref } from "vue"
import type { ICitaOdontologicas } from "@/modules/Asistencial/interfaces"
import { G, Path, SVG, Rect } from '@svgdotjs/svg.js'
import { getCompontInChildrens } from "@/modules/Core/usings/generales"
import vuexStore from "@/store"

// El estado compartido (diligenciados, dientes ausentes y dientes presentes) vive
// en el store Vuex 'odontograma'. Se importa la instancia directamente (y no
// useStore()) porque este composable tambien se invoca fuera de un setup, p.ej.
// dentro del onMounted de OdontogramaModalButton.
export interface cuadranteItem {
  num: number, x: number, y: number
}
export interface optionsOdontograma {
  comp?: IFormComponent
  isView?: boolean
  alterDiente?: (group: G, num: number, event: Event) => void
  datos?: ICitaOdontologicas[]
  data?: ICitaOdontologicas[]
}
const centrar = {
  centro: [40, 30],
  arriba: [120, -30],
  abajo: [120, -30],
  izquierda: [-30, 90],
  derecha: [-30, 90],
  lado_superior: [250, 10],
  lado_inferior: [250, -90],
}
const cuadrantes = [
  {desde: 18, hasta: 11, aumentador: -1, x: 0, y: 0, separador: 11, tipo: 'ADULTO'},
  {desde: 21, hasta: 28, aumentador: 1, x: 8.2, y: 0, separador: 21, tipo: 'ADULTO'},
  {desde: 55, hasta: 51, aumentador: -1, x: 3, y: 1, separador: 51, tipo: 'MENOR'},
  {desde: 61, hasta: 65, aumentador: 1, x: 8.2, y: 1, separador: 61, tipo: 'MENOR'},
  {desde: 85, hasta: 81, aumentador: -1, x: 3, y: 2, separador: 81, tipo: 'MENOR'},
  {desde: 71, hasta: 75, aumentador: 1, x: 8.2, y: 2, separador: 71, tipo: 'MENOR'},
  {desde: 48, hasta: 41, aumentador: -1, x: 0, y: 3, separador: 41, tipo: 'ADULTO'},
  {desde: 31, hasta: 38, aumentador: 1, x: 8.2, y: 3, separador: 11, tipo: 'ADULTO'}
]

export const useOdontograma = (origen: string) => {
  const dibujadosDefault = ref<{ num: number, group: G}[]>([])
  const odontogramas: Ref<IConfOdontogramas[]> = ref([] as IConfOdontogramas[])
  const marcacionSeleccionada: Ref<IConfOdontogramasConvenciones> = ref({} as IConfOdontogramasConvenciones)
  const marcaciones: Ref<IConfOdontogramasConvenciones[]> = ref([] as IConfOdontogramasConvenciones[])
  const marcacionAusente: Ref<IConfOdontogramasConvenciones | null> = ref(null)
  const CLASS_CONVENCION = 'convencion'
  const CLASS_AUSENTE = 'ausente'
  const NAME_ODONTOGRAMA = 'Odontograma'
  const NAME_CONTROL_PLACA = 'Control placa'
  const CONVENCIONES_DIENTE_PRESENTE = [
        'DIENTE_SANO',
        'ERUPCION_BUEN_ESTADO',
        'ERUPCION_MAL_ESTADO',
        'ERUPCION_ARRIBA',
        'ERUPCION_ABAJO',
        'ERUPCION_DOBLE',
        'EROSION_ARRIBA',
        'EROSION_ABAJO',
        'EROSION_DOBLE',
        'CARIES_CAVITACIONAL',
        'CARIES_NO_CAVITACIONAL',
        'CARIADO_CAVITACIONAL',
        'CARIADO_NO_CAVITACIONAL',
        'RESINA',
        'RESINA_RECURRENTE',
        'AMALGAMA',
        'AMALGAMA_RECURRENTE',
        'CORONA_BUEN_ESTADO',
        'CORONA_MAL_ESTADO',
        'NUCLEO_BUEN_ESTADO',
        'NUCLEO_MAL_ESTADO',
        'VIDRIO',
        'VIDRIO_RECURRENTE',
        'SELLANTE_PRESENTE',
        'SELLANTE_INDICADO',
        'FRACTURADO',
        'ENDODONCIA_INDICADA',
        'ENDODONCIA_REALIZADA',
        'EXODONCIA_INDICADA',
        'EXODONCIA_SIMPLE_INDICADA',
        'EXODONCIA_QUIRURGICA',
        'PROCEDIMIENTO_REALIZADO',
        'PROTESIS_BUEN_ESTADO',
        'PROTESIS_MAL_ESTADO',
        'PROTESIS_FIJA_BUEN_ESTADO',
        'PROTESIS_FIJA_MAL_ESTADO',
        'TEMPORAL']
  const CONVENCIONES_DIENTE_AUSENTE = ['PROTESIS_BUEN_ESTADO','PROTESIS_MAL_ESTADO','DIENTE_AUSENTE', 'EXODONCIA_REALIZADA', 'EXODONCIA_INDICADA', 'EXODONCIA_SIMPLE_INDICADA', 'EXODONCIA_QUIRURGICA_IND','EXODONCIA_QUIRURGICA']

  const isBorrar = ref(false)
  
  let store: ICitaOdontologicas[] = [] as ICitaOdontologicas[]
  let soloLectura = false
  let renderizando = false
  const sincronizarAusentes = (odontograma: IConfOdontogramas, cittId: number) => {
    if (soloLectura || renderizando || odontograma.coodNombre !== NAME_ODONTOGRAMA) return
    const ausentes = store
      .filter(e => CONVENCIONES_DIENTE_AUSENTE.includes(e.ciodConvencion as string))
      .map(e => ({ ...e, ciodConvencion: 'DIENTE_AUSENTE' }))
    vuexStore.commit('odontograma/setAusentesPorAtencion', { cittId, ausentes })
  }
  const setDatosOdontologia = (odontograma: IConfOdontogramas, params: {
    datos: ICitaOdontologicas[]
    comp?: IFormComponent
    cittId: number
  }) => {
      const { datos, comp, cittId: cittIdDatos } = params
      
      vuexStore.commit('odontograma/setDiligenciado', {
        origen,
        nombre: odontograma.coodNombre as string,
        diligenciado: datos.length > 0
      })
      if (comp) {
        if (comp.odontograma == 'Odontograma') {
          const dienteConvencion = datos.filter(e => CONVENCIONES_DIENTE_PRESENTE.some( a => a == e.ciodConvencion )).map(e => parseInt(e.ciodDiente as string))
          const dienteConConvenciones = dienteConvencion.filter((item, index) => dienteConvencion.indexOf(item) === index )
          const cantDientes = dienteConConvenciones.length
          const cariadosNoCavitacional = getCompontInChildrens(comp, 'SU_34B_3')
          if (cariadosNoCavitacional) {
            const cariadosConNoCavitacional = getDientesByConvenciones(datos, ['CARIADO_NO_CAVITACIONAL', 'CARIES_NO_CAVITACIONAL'])
            cariadosNoCavitacional.value = cariadosConNoCavitacional.length.toString()
          }
          const cariadosCavitacional = getCompontInChildrens(comp, 'SU_34B_4')
          if (cariadosCavitacional){ 
            const cariadosConCavitacional = getDientesByConvenciones(datos, ['CARIADO_CAVITACIONAL', 'CARIES_CAVITACIONAL'])
            cariadosCavitacional.value = cariadosConCavitacional.length.toString()
          }
          const obturados = getCompontInChildrens(comp, 'SU_34B_5')
          if (obturados) {
            const dienteObturados = getDientesByConvenciones(datos, ['AMALGAMA', 'RESINA', 'VIDRIO'])
            obturados.value = dienteObturados.length.toString()
          }
          const perdidos = getCompontInChildrens(comp, 'SU_34B_6')
          if (perdidos){
            const dientePerdido = getDientesByConvenciones(datos, ['EXODONCIA_REALIZADA'])
            perdidos.value = dientePerdido.length.toString()
          }
          const sanos = getCompontInChildrens(comp, 'SU_34B_2')
          if (sanos) {
            const dienteSanos = getDientesByConvenciones(datos, ['DIENTE_SANO'])
            sanos.value = dienteSanos.length.toString()
          } 
          const presentes = getCompontInChildrens(comp, 'SU_34B_7')
          if (presentes) presentes.value = cantDientes.toString()
          // Se publica en el store para que el Control de placa lo tome de ahi
          if (cittIdDatos) {
            vuexStore.commit('odontograma/setDientesPresentes', { cittId: cittIdDatos, cantidad: cantDientes })
          }
        } else if(comp.odontograma == NAME_CONTROL_PLACA) {
          const superficiePlaca = getCompontInChildrens(comp, 'SU_35B_1')
          const cantCariado = (datos.filter(e => e.ciodConvencion == 'PLACA').length)
          if (superficiePlaca) superficiePlaca.value = cantCariado.toString()
          const indicePlaca = getCompontInChildrens(comp, 'SU_35B_2')
          // "Dientes presentes" lo publica el Odontograma en el store. Antes se leia
          // del DOM (getElementById('SU_34B_7')), que devolvia null y lanzaba
          // TypeError cuando el Control de placa se abria sin el odontograma montado.
          // Se conserva el DOM solo como respaldo.
          const presentesStore = cittIdDatos
            ? (vuexStore.getters['odontograma/dientesPresentesDe'](cittIdDatos) as number | null)
            : null
          const cantDientes = presentesStore ?? parseInt(
            (document.getElementById('SU_34B_7') as HTMLInputElement | null)?.value ?? ''
          )
          if (!Number.isFinite(cantDientes) || cantDientes <= 0) {
            window.notificacion.notificacion("info", "Debe llenar el campo dientes presentes en el odontograma")
            if (indicePlaca) indicePlaca.value = ''
            return false
          }
          if (indicePlaca) indicePlaca.value = cantCariado > 0 ? ( ( cantCariado / (cantDientes * 5) ) * 100 ).toFixed(2).toString(): '0'
        }
      }
  }
    
  const getDientesByConvenciones = (datos: ICitaOdontologicas[] , convenciones: string[]) => {
    const data =  datos.filter(e => convenciones.some( a => a == e.ciodConvencion ) ).map(e => parseInt(e.ciodDiente as string))
    const result = data.filter((item, index) => data.indexOf(item) === index )
    return result
  }
  const validateOdontograma = () => {
      // El getter devuelve {} si el origen no fue inicializado (antes esto lanzaba
      // TypeError con Object.keys(undefined) y rompia el cambio de tab)
      const registro = vuexStore.getters['odontograma/diligenciadosPorOrigen'](origen) as Record<string, boolean>
      const cantOdontogramas = Object.keys(registro).length
      const noValidos = Object.keys(registro).filter(key => !registro[key]).join(', ')
      const result = { isValid: noValidos.length === 0 && cantOdontogramas > 0, noValidos }
      return result
  }
  const setValidateOdontograma = async  (coodNombre: string, diligenciado: boolean) => {
      vuexStore.commit('odontograma/setDiligenciado', { origen, nombre: coodNombre, diligenciado })
  }
  const generarCuadrante = ({ desde, hasta, aumentador, x, y }: { desde: number, hasta: number, aumentador: number, x: number, y: number }) => {
    const cuadrante = [] as cuadranteItem[]
    if (aumentador > 0) {
      for (let i = desde; i <= hasta; i += aumentador) {
        cuadrante.push({ num: i, x: x, y })
        x++
      }
    } else {
      for (let i = desde; i >= hasta; i += aumentador) {
        cuadrante.push({ num: i, x: x, y })
        x++
      }
    }
    return cuadrante
  }
  const loadSvgIntoGroup = (group: G, svgContent: string): Promise<void> =>{
    return new Promise((resolve) => {
      group.svg(svgContent)
      requestAnimationFrame(() => {
        resolve()
      })
    })
  }
  const generarSVG = async (odontograma: IConfOdontogramas, cittId: number, print: boolean = false, options?: optionsOdontograma) => {
    const { comp, isView = false, data = [], datos = [] } = options || {}
    soloLectura = isView || print
    const draw = SVG().size(1500, 700)

    const spacingX = 75
    const spacingY = 150
    const scale = 0.08 // para escalar tu diente SVG
    const toothSVG = odontograma.coodSvgDiente?.replace(/^<svg[^>]*>/, '').replace(/<\/svg>$/, '')
    
    marcaciones.value = odontograma.convenciones || []
    if(marcaciones.value.length == 1 || odontograma.coodNombre == NAME_CONTROL_PLACA ){
      marcacionSeleccionada.value = marcaciones.value[0]
    } 
    marcacionAusente.value = marcaciones.value.find(e => e.identificador === 'DIENTE_AUSENTE') as IConfOdontogramasConvenciones
    
    const dibujados = [] as { num: number, group: G}[]
    for (const item of cuadrantes) {
      for (const { num, x, y } of generarCuadrante(item)) {
          const groupCuadrante = draw.group()
          const px = x * spacingX
          const py = y * spacingY + 50
          const group = groupCuadrante.group().translate(px, py).scale(scale)
          await loadSvgIntoGroup(group, toothSVG as string)
          group.attr('data-num', num)
          // if(!isView && !print) group.on('click', (e) => alterDiente(group, num, e, odontograma, print, cittId))
          groupCuadrante.text(String(num))
            .font({ size: 14, anchor: 'middle' })
            .move(px + 23, py - 35)
          dibujados.push({ num, group})
      }
    }
    let dataOdon = datos.concat(data)
    // Puente Odontograma -> Control de placa: los dientes marcados ausentes en el
    // odontograma se reflejan aca. El getter ya acota a la atencion en curso, asi
    // que los ausentes de otra atencion no interfieren.
    const ausentesAtencion = vuexStore.getters['odontograma/ausentesPorAtencion'](cittId) as ICitaOdontologicas[]
    if(odontograma.coodNombre == NAME_CONTROL_PLACA && isView == false && !print){
        dataOdon = dataOdon.filter(e => e.ciodConvencion !== 'DIENTE_AUSENTE' && !ausentesAtencion.some(d => d.ciodDiente === e.ciodDiente))
        dataOdon = dataOdon.concat(ausentesAtencion.map(e => ({ ...e, coodId: odontograma.coodId } as ICitaOdontologicas)))
    }
    // `store` es el acumulador de marcas y ademas actua como guard anti-doble-pintado
    // en pintarOdontograma. Se reinicia ANTES de pintar porque este bloque redibuja
    // el odontograma completo desde dataOdon y setMarcacion lo vuelve a poblar.
    // Sin esto, al regenerar sin registros en BD (data vacio, p.ej. el boton
    // "todos sanos" sobre un odontograma nuevo) el store conservaba las marcas,
    // pintarOdontograma las daba por pintadas y no se dibujaba ninguna convencion.
    store = [] as ICitaOdontologicas[]
    renderizando = true
    try {
      dibujados.forEach(({ num, group } : { num: number, group: G}) => {
        const existing = dataOdon.filter(e => e.ciodDiente === String(num))
        if (existing.length > 0) {
          pintarOdontograma(existing, group, odontograma, print, cittId)
        }
        /* if(odontograma.coodNombre == NAME_CONTROL_PLACA && odontogramaAusentes.value.some(e => parseInt(e.ciodDiente as string) == num && e.cittId == cittId)){
          group.group().addClass(CLASS_AUSENTE)
          group.find('path').forEach((p) => (p as Path).fill(marcacionAusente.color || ''))
        } */
      })
    } finally {
      renderizando = false
    }
    sincronizarAusentes(odontograma, cittId)
    if (!soloLectura) setDatosOdontologia(odontograma, { datos: store, comp, cittId })
    dibujadosDefault.value = dibujados
    const xml = draw.svg()
    return xml
  }
  const setDientesSanos = async (tipo: string, odontograma: IConfOdontogramas, print: boolean, cittId: number) => {
    const rangos = cuadrantes.filter(e => e.tipo === tipo)
    const marcacion = marcaciones.value.find((m: IConfOdontogramasConvenciones) => m.identificador == 'DIENTE_SANO')
    dibujadosDefault.value.filter(e => rangos.some(a => (a.aumentador < 0 &&  a.hasta <= e.num && a.desde >= e.num) || (a.aumentador > 0 &&  a.desde <= e.num && a.hasta >= e.num) )).forEach(({ num, group } : { num: number, group: G}) => {
        const exist = store.find(e => parseInt(e.ciodDiente as string) == num && e.cittId == cittId)
        if(!exist) setMarcacion(group, num, null, marcacion as IConfOdontogramasConvenciones, odontograma, print, cittId)
    })
  }
  const setDientesAusentes = async (odontograma: IConfOdontogramas, print: boolean, cittId: number) => {
    dibujadosDefault.value.forEach(({ num, group } : { num: number, group: G}) => {
        const exist = store.find(e => parseInt(e.ciodDiente as string) == num && e.cittId == cittId)
        if(!exist) setMarcacion(group, num, null, marcacionAusente.value as IConfOdontogramasConvenciones, odontograma, print, cittId)
    })
  }
  const pintarOdontograma = (dientes: ICitaOdontologicas[], group: G, odontograma: IConfOdontogramas, print: boolean, cittId: number) => {
    dientes.forEach((diente) => {
      const marcacion = marcaciones.value.find((m: IConfOdontogramasConvenciones) => m.identificador == diente.ciodConvencion)
      const exist = store.find(e => e.ciodDiente == diente.ciodDiente && e.ciodParte == diente.ciodParte && e.cittId == cittId && e.ciodConvencion == diente.ciodConvencion)
      if (exist) return
      if (!marcacion) {
        if (!store.some(e => e.ciodConvencion === diente.ciodConvencion)) {
          window.notificacion.notificacion('warning', `La convención ${diente.ciodConvencion} no está configurada. Sus datos se conservarán.`)
        }
        store.push({ ...diente, cittId, coodId: odontograma.coodId })
        return
      }
      setMarcacion(group, parseInt(diente.ciodDiente as string), diente.ciodParte, marcacion, odontograma, print, cittId)
    })
  }
  const setMarcacion = (group: G,num: number, part: string | null | undefined, marcacion: IConfOdontogramasConvenciones, odontograma: IConfOdontogramas, print: boolean, cittId: number) => {
    if (!marcacion) throw new Error('La convención seleccionada no está configurada')
    if (marcacion.tipoConvencion == 1) {
      const existing = group.findOne(`.${CLASS_CONVENCION}`)
      const isAusente = group.findOne(`.${CLASS_AUSENTE}`)
      if (!print && (existing || isAusente)) {
        window.notificacion.notificacion("warning", "Ya tiene una convencion registrada")
        return false
      }
      if(marcacion.svg){
        const bbox = group.bbox()
        group.group().addClass(CLASS_CONVENCION).scale(2.5).translate(bbox.x-280, bbox.y-70).svg(marcacion.svg?.replace('<svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 500 500" width="100%" height="100%" style="shape-rendering:geometricPrecision; text-rendering:geometricPrecision; image-rendering:optimizeQuality; fill-rule:evenodd; clip-rule:evenodd" xmlns:xlink="http://www.w3.org/1999/xlink">','').replace(/^<svg[^>]*>/, '').replace(/<\/svg>$/, '') || '')
      } else {
        group.group().addClass(CLASS_AUSENTE)
        group.find('path').forEach((p) => (p as Path).fill(marcacion.color || ''))
        group.find('rect').forEach((r) => (r as Rect).fill(marcacion.color || ''))
      }
    } else {
      if(marcacion.svg){
        if (!part) return false
        const pathOriginal = group.findOne(`#${part}`)
        if (!pathOriginal) return false
        const existing = group.findOne(`.${CLASS_CONVENCION}_${part}`)
        const existingPart = group.findOne(`.${CLASS_CONVENCION}`)
        const isAusente = group.findOne(`.${CLASS_AUSENTE}`)
        if (!print && (existing || existingPart || isAusente)) {
          window.notificacion.notificacion("warning", "Ya tiene una convencion registrada")
          return false
        }
        (pathOriginal as Path).fill('#FFFFFF')
        store = store.filter(e => e.ciodDiente !== num.toString() || e.ciodParte !== part)
        const bbox = (pathOriginal as Path).bbox()
        const positionX = centrar[part as keyof typeof centrar]?.[0]
        const positionY = centrar[part as keyof typeof centrar]?.[1]
        group.group().addClass(`convencion_${part}`).scale(0.5).translate(bbox.x + positionX, bbox.y + positionY).svg(marcacion.svg?.replace('<svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 500 500" width="100%" height="100%" style="shape-rendering:geometricPrecision; text-rendering:geometricPrecision; image-rendering:optimizeQuality; fill-rule:evenodd; clip-rule:evenodd" xmlns:xlink="http://www.w3.org/1999/xlink">','').replace(/^<svg[^>]*>/, '').replace(/<\/svg>$/, '') || '')
      } else {
        const pathOriginal = group.findOne(`#${part}`)
        if (!pathOriginal) return false
        const isAusente = group.findOne(`.${CLASS_AUSENTE}`)
        if (isAusente) {
          window.notificacion.notificacion("warning", "El diente se encuentra ausente, no puede agregar convenciones")
          return false
        }
        const superficie = pathOriginal as Path
        group.findOne(`.${CLASS_CONVENCION}_${part}`)?.remove()
        superficie.fill(marcacion.color || '')
        store = store.filter(e => e.ciodDiente !== num.toString() || e.ciodParte !== part)
      }
    }
    const newStore = {
      cittId: cittId,
      coodId: odontograma.coodId,
      ciodDiente: num.toString(),
      ciodConvencion: marcacion.identificador,
      ciodParte: marcacion.tipoConvencion == 1 ? null : part
    } as ICitaOdontologicas
    if(odontograma.coodNombre != NAME_CONTROL_PLACA || marcacion.identificador != 'DIENTE_AUSENTE') {
      store.push(newStore)
    }
    if (!print) sincronizarAusentes(odontograma, cittId)
  }
  const alterDiente = (group: G, num: number, event: Event, odontograma: IConfOdontogramas, print: boolean, cittId: number) => {
    const elem = event.target as HTMLElement
    if (elem) {
      if (isBorrar.value) {
        const color_original = "#FFFFFF"
        const existing = group.findOne(`.${CLASS_CONVENCION}`)
        const isAusente = group.findOne(`.${CLASS_AUSENTE}`)
        const padrePartClass = (<HTMLElement>elem.parentNode?.parentNode).getAttribute("class");
        const existingPart = group.findOne(`.${padrePartClass}`)
        if(isAusente && odontograma.coodNombre == NAME_ODONTOGRAMA){
          isAusente.remove()
          group.find('path').forEach((p) => (p as Path).fill(color_original || ''))
          group.find('rect').forEach((r) => (r as Rect).fill(color_original || ''))
          store = store.filter((e) => e.ciodDiente != num.toString())
        } else if (existing) {
          existing.remove()
          group.find('g[class^="convencion_"]').forEach(e => e.remove())
          group.find('path').forEach(p => (p as Path).fill(color_original))
          group.find('rect').forEach(r => (r as Rect).fill(color_original))
          store = store.filter((e) => e.ciodDiente != num.toString())
        } else if (existingPart && !isAusente) {
          existingPart.remove()
          store = store.filter((e) => e.ciodDiente != num.toString() || e.ciodParte != padrePartClass?.split("_")?.[1])
        } else if (!isAusente) {
          //Borrar color
          const pathOriginal = group.findOne(`#${elem.getAttribute("id")}`)
          if (!pathOriginal) { return false }
          (pathOriginal as Path).fill(color_original || '')
          store = store.filter((e) => e.ciodDiente !== num.toString() || e.ciodParte !== elem.getAttribute("id"))
        }
        if (!print) sincronizarAusentes(odontograma, cittId)
      } else if (Object.keys(marcacionSeleccionada.value).length) {
        const marcacion = marcacionSeleccionada.value
        setMarcacion(group, num, elem.getAttribute("id") || '', marcacion, odontograma,print, cittId)
      } else {
        window.notificacion.notificacion("info", "Debe seleccionar una marcación")
      }
    }
  }
  const setMarcacionSeleccionada = (marca: IConfOdontogramasConvenciones) => {
    marcacionSeleccionada.value = marca
  }
  const setIsBorrar = (valor: boolean) => {
    isBorrar.value = valor
  }
  const dibujarEnElDOM = (id: string, xml: string, odontograma: IConfOdontogramas, cittId: number, print: boolean, isView: boolean = false) => {
    const draw = SVG().addTo(id).size(1500, 700).svg(xml)
    draw.find('[data-num]').forEach((element) => {
      const g = element as G
      const num = parseInt(String(g.attr('data-num')))
      if (!isView) g.on('click', (e: Event) => alterDiente(g, num, e, odontograma, print, cittId))
    })
  }
  const getStore = () => {
    return store
  }
  const getDientesPresentesAtencion = (cittId: number): number | null =>
    vuexStore.getters['odontograma/dientesPresentesDe'](cittId)
  const getAusentesAtencion = (cittId: number) =>
    vuexStore.getters['odontograma/ausentesPorAtencion'](cittId) as ICitaOdontologicas[]
  /**
   * Estado de diligenciamiento (solo lectura). Antes era un ref de modulo que
   * cualquier onUnmounted borraba por completo; ahora vive en el store y se
   * limpia de forma explicita con limpiarOrigen / limpiarAtencion.
   */
  const datosOdontologia = computed(() => vuexStore.state.odontograma.diligenciados)

  /** Limpia el diligenciamiento de este origen (historia | view | impresion) */
  const limpiarOrigen = () => vuexStore.commit('odontograma/limpiarOrigen', origen)

  /** Limpia ausentes y dientes presentes de una atencion puntual */
  const limpiarAtencion = (cittId: number) => vuexStore.commit('odontograma/limpiarAtencion', cittId)

  /** Reinicia todo el estado del odontograma */
  const resetOdontograma = () => vuexStore.commit('odontograma/resetOdontograma')

  // El origen 'view' es efimero (historico / consulta en solo lectura) y su
  // diligenciamiento no lo consume nadie: validateOdontograma solo se invoca con
  // 'historia'. Se descarta al desmontar para no dejarlo colgado en el store.
  // Los demas origenes NO se limpian aca a proposito: hay varias instancias vivas
  // a la vez y borrarlas en un unmount arbitrario fue el bug original (cerrar el
  // "Historico" dejaba la validacion de la historia en undefined).
  // getCurrentInstance evita registrar el hook cuando el composable se invoca
  // fuera de un setup (p.ej. dentro del onMounted de OdontogramaModalButton).
  if (origen === 'view' && getCurrentInstance()) {
    onUnmounted(() => { limpiarOrigen() })
  }

  return {
    odontogramas,
    datosOdontologia,
    limpiarOrigen,
    limpiarAtencion,
    resetOdontograma,
    marcacionSeleccionada,
    isBorrar,
    marcaciones,
    marcacionAusente,
    getStore,
    getAusentesAtencion,
    getDientesPresentesAtencion,
    setDatosOdontologia,
    validateOdontograma,
    generarSVG,
    pintarOdontograma,
    setMarcacionSeleccionada,
    setValidateOdontograma,
    setIsBorrar,
    dibujarEnElDOM,
    setDientesSanos,
    setDientesAusentes
  }
}
